/**
 * PricebookPicker (PRICEBOOK-PICK-01) — the one "add from the pricebook" control:
 * type and the best matches drop down (↓ ↑ Enter, Esc); "See all results and
 * details" or Browse opens the full pricebook window (PricebookBrowser) with the
 * same search; nothing found offers "add it by hand" and Browse. Every desktop
 * picker uses it so they all search, look and behave the same.
 */
import { useEffect, useRef, useState } from "react";
import { ArrowRight, BookOpen, Search } from "lucide-react";
import { cn } from "@/lib/cn";
import { scrollActiveIntoView, useListNav } from "@/lib/keys";
import type { Item } from "./items";
import { itemCostOf, itemPrice, usePricebookSearch, type PricebookFilters } from "./pricebookSearch";
import { PricebookBrowser, pbMoney, useDebounced } from "./PricebookBrowser";

export function PricebookPicker({
  onPick,
  placeholder = "Search the pricebook — part #, model, size or brand…",
  pickLabel = "Add",
  filters,
  onNoMatch,
  noMatchLabel = "Add it by hand",
  showCost = true,
  browseMulti = true,
  autoFocus,
  className,
}: {
  onPick: (it: Item) => void;
  placeholder?: string;
  /** The add button's words in the pricebook window — "Add to Job". */
  pickLabel?: string;
  filters?: PricebookFilters;
  /** Nothing matched — the caller's own way to add it (the typed text is passed along). */
  onNoMatch?: (term: string) => void;
  noMatchLabel?: string;
  /** Show our cost beside the price (office pickers). */
  showCost?: boolean;
  /** The pricebook window stays open after an add (false: one pick fills a form). */
  browseMulti?: boolean;
  autoFocus?: boolean;
  className?: string;
}) {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [browse, setBrowse] = useState<string | null>(null);
  const dq = useDebounced(term, 120);
  const typed = dq.trim().length >= 2;
  const { data, isFetching } = usePricebookSearch(dq, { ...filters, limit: 8, enabled: typed });
  const rows = typed && term.trim().length >= 2 ? (data ?? []) : [];
  const settled = typed && !isFetching && dq === term;
  const listOpen = open && rows.length > 0;
  const noMatch = open && settled && (data ?? []).length === 0;
  const listRef = useRef<HTMLDivElement>(null);

  const pick = (it: Item) => { onPick(it); setTerm(""); setOpen(false); };
  const openBrowser = () => { setBrowse(term); setOpen(false); };
  const nav = useListNav({
    count: rows.length,
    open: listOpen,
    typed: term.trim().length >= 2,
    onPick: (i) => { const it = rows[i]; if (it) pick(it); },
    onClose: () => setOpen(false),
  });
  useEffect(() => { if (listOpen) scrollActiveIntoView(listRef.current, nav.active); }, [listOpen, nav.active]);

  return (
    <div className={cn("relative", className)}>
      <div className="flex items-center gap-2">
        <label className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            role="combobox"
            aria-expanded={listOpen}
            aria-autocomplete="list"
            aria-label="Search the pricebook"
            autoComplete="off"
            autoFocus={autoFocus}
            value={term}
            onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => window.setTimeout(() => setOpen(false), 150)}
            onKeyDown={nav.onKeyDown}
            placeholder={placeholder}
            className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25"
          />
        </label>
        <button
          type="button"
          onClick={openBrowser}
          title="Open the pricebook — filters and details"
          className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-lg bg-white px-3 text-sm font-semibold text-slate-700 ring-1 ring-inset ring-slate-200 transition-colors hover:bg-slate-50 hover:text-brand-800"
        >
          <BookOpen className="h-4 w-4" /> Browse
        </button>
      </div>

      {listOpen && (
        <div data-glass="panel" className="absolute left-0 right-0 z-30 mt-1 overflow-hidden rounded-xl">
          <div ref={listRef} role="listbox" aria-label="Pricebook matches" className="max-h-80 overflow-y-auto py-1">
            {rows.map((it, i) => {
              const price = itemPrice(it);
              const cost = itemCostOf(it);
              const onHand = it.qty_in_stock != null && Number(it.qty_in_stock) > 0 ? Number(it.qty_in_stock) : null;
              return (
                <button
                  key={it.id}
                  type="button"
                  role="option"
                  aria-selected={nav.active === i}
                  data-index={i}
                  tabIndex={-1}
                  onMouseDown={(e) => { e.preventDefault(); pick(it); }}
                  onMouseEnter={() => nav.setActive(i)}
                  className={cn("flex w-full items-start gap-3 px-3 py-2 text-left", nav.active === i ? "bg-brand-50" : "hover:bg-slate-50")}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-slate-800">{it.description || it.part_number}</span>
                    <span className="mt-0.5 block truncate text-xs text-slate-500">
                      <span className="font-mono text-slate-600">{it.part_number}</span>
                      {[it.manufacturer, it.category].filter(Boolean).map((x) => ` · ${x}`).join("")}
                      {showCost && cost != null ? ` · cost ${pbMoney(cost)}` : ""}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block text-sm font-semibold tabular-nums text-slate-800">{pbMoney(price)}</span>
                    {onHand != null && <span className="block text-xs font-medium text-emerald-700">{onHand} on hand</span>}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); openBrowser(); }}
            className="flex w-full items-center justify-between border-t border-slate-100 px-3 py-2 text-left text-sm font-semibold text-brand-700 hover:bg-brand-50"
          >
            See all results and details <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {noMatch && (
        <div data-glass="panel" className="absolute left-0 right-0 z-30 mt-1 rounded-xl px-3 py-2.5">
          <p className="text-sm text-slate-600">Nothing in the pricebook matches &ldquo;{term.trim()}&rdquo;.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {onNoMatch && (
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onNoMatch(term.trim()); setTerm(""); setOpen(false); }}
                className="rounded-full bg-brand-600 px-3 py-1 text-sm font-semibold text-white hover:bg-brand-700"
              >
                {noMatchLabel}
              </button>
            )}
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); openBrowser(); }}
              className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"
            >
              Browse the Pricebook
            </button>
          </div>
        </div>
      )}

      <PricebookBrowser
        open={browse != null}
        initialQuery={browse ?? ""}
        onClose={() => setBrowse(null)}
        onPick={onPick}
        pickLabel={pickLabel}
        multi={browseMulti}
        filters={filters}
      />
    </div>
  );
}
